import React, { useState } from 'react';
import { Search, Edit3, Trash2, PowerOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { BookingServiceResponseDto, MeetingType } from '../../types/booking';
import Spinner from '../Spinner';

const ITEMS_PER_PAGE = 10;

interface ServiceTableProps {
  services: BookingServiceResponseDto[];
  loading: boolean;
  onEdit?: (service: BookingServiceResponseDto) => void;
  onDelete?: (service: BookingServiceResponseDto) => void;
  onDeactivate?: (service: BookingServiceResponseDto) => void;
}

export default function ServiceTable({ services, loading, onEdit, onDelete, onDeactivate }: ServiceTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Inactive'>('All');
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = services.filter((s) => {
    const matchesStatus = statusFilter === 'All' || (statusFilter === 'Active' ? s.active : !s.active);
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginatedData = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center bg-white p-4 rounded-lg border border-strivre-lightnavy">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-strivre-mutedtext" size={18} />
          <input
            type="text"
            placeholder="Search services..."
            className="input-base pl-10 h-10"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <select
          className="input-base h-10 w-full sm:w-48"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as any); setCurrentPage(1); }}
        >
          <option value="All">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border border-strivre-lightnavy overflow-hidden">
        {loading && (
          <div className="p-8 flex justify-center items-center">
            <Spinner size="h-8 w-8" />
            <p className="ml-3 text-strivre-mutedtext">Loading services...</p>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-strivre-mutedtext">No services found.</div>
        )}

        {/* Mobile View */}
        <div className="md:hidden divide-y divide-slate-100">
          {!loading && paginatedData.map((s) => (
            <div key={s.id} className="p-5 space-y-3">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <h4 className="text-sm font-bold text-strivre-navy">{s.name}</h4>
                  <p className="text-xs text-strivre-mutedtext">
                    {s.durationMinutes} min ·{" "}
                    {s.price != null && s.price > 0
                      ? `$${s.price.toFixed(2)}`
                      : "Free"}
                  </p>
                </div>
                <span className={`px-2.5 py-0.5 rounded text-xs font-semibold border ${s.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-strivre-softgrey text-slate-500 border-strivre-lightnavy'}`}>
                  {s.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onEdit?.(s)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg" title="Edit"><Edit3 size={16} /></button>
                <button onClick={() => onDeactivate?.(s)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg" title="Deactivate"><PowerOff size={16} /></button>
                <button onClick={() => onDelete?.(s)} className="p-2 bg-strivre-softgrey text-strivre-burgundy rounded-lg" title="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-strivre-softgrey border-b border-strivre-lightnavy">
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Duration</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Price</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Meeting Type</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-xs font-bold text-strivre-mutedtext uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.map((s) => (
                <tr key={s.id} className="hover:bg-strivre-softgrey/60 transition-colors">
                  <td className="px-4 py-3 font-semibold text-strivre-navy">{s.name}</td>
                  <td className="px-4 py-3 text-strivre-darktext">{s.durationMinutes} min</td>
                  <td className="px-4 py-3 text-strivre-darktext">
                    {s.price != null && s.price > 0
                      ? `$${s.price.toFixed(2)}`
                      : "Free"}
                  </td>
                  <td className="px-4 py-3 text-strivre-darktext">
                    {s.meetingType === MeetingType.Online
                      ? "Online"
                      : s.meetingType === MeetingType.OnSite
                        ? "On-Site"
                        : "Phone"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded text-xs font-semibold border ${s.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-strivre-softgrey text-slate-500 border-strivre-lightnavy'}`}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-strivre-darktext">{new Date(s.createdDate).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => onEdit?.(s)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg hover:bg-strivre-lightnavy" title="Edit"><Edit3 size={16} /></button>
                      <button onClick={() => onDeactivate?.(s)} className="p-2 bg-strivre-softgrey text-strivre-navy rounded-lg hover:bg-strivre-lightnavy" title="Deactivate"><PowerOff size={16} /></button>
                      <button onClick={() => onDelete?.(s)} className="p-2 bg-strivre-softgrey text-strivre-burgundy rounded-lg hover:bg-strivre-lightburgundy" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-4 py-3 bg-white border-t border-strivre-lightnavy flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-strivre-mutedtext font-medium">
              Showing {paginatedData.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} services
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-strivre-navy hover:bg-strivre-softgrey disabled:opacity-50">
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-medium text-strivre-darktext min-w-[4.5rem] text-center">Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-strivre-navy hover:bg-strivre-softgrey disabled:opacity-50">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
